/**
 * Prymitywy mobilnego trybu tabel (<md), wspolne dla DataTable i AdminTable:
 * lista dwuliniowa z miara i mikro-paskiem, przelacznik Lista/Tabela, arkusz
 * sortowania i filtrow kolumn oraz pelnoekranowe szczegoly wiersza.
 * Wzorzec: `.claude/skills/farmaster-ui-standard/references/mobile.md`.
 */
import { useVirtualizer } from "@tanstack/react-virtual";
import {
	ArrowDown,
	ArrowUp,
	ArrowUpDown,
	ChevronLeft,
	ChevronRight,
	Columns3,
	List,
	Loader2,
} from "lucide-react";
import * as React from "react";
import { BottomSheet, SheetFooterActions } from "@/components/ui/bottom-sheet";
import { usePageChromeState } from "@/components/ui/page-chrome";
import { readTableMode, type TableMobileMode, writeTableMode } from "@/components/ui/table-mobile";
import { cn } from "@/lib/utils";

/**
 * Tryb Lista/Tabela tabeli na telefonie, zapamietany per `storageId`.
 * `defaultMode` ("table") tylko dla tabel, w ktorych glownym zadaniem jest
 * porownywanie wielu kolumn naraz — patrz references/mobile.md.
 */
export function useTableMode(
	storageId: string,
	defaultMode?: TableMobileMode,
): [TableMobileMode, (mode: TableMobileMode) => void] {
	const [mode, setModeState] = React.useState<TableMobileMode>(() =>
		readTableMode(storageId, defaultMode),
	);
	React.useEffect(() => {
		setModeState(readTableMode(storageId, defaultMode));
	}, [storageId, defaultMode]);
	const setMode = React.useCallback(
		(next: TableMobileMode) => {
			setModeState(next);
			writeTableMode(storageId, next);
		},
		[storageId],
	);
	return [mode, setMode];
}

export function TableModeToggle({
	mode,
	onChange,
	className,
}: {
	mode: TableMobileMode;
	onChange: (mode: TableMobileMode) => void;
	className?: string;
}) {
	const item = (value: TableMobileMode, label: string, icon: React.ReactNode) => (
		<button
			type="button"
			aria-pressed={mode === value}
			aria-label={label}
			title={label}
			onClick={() => onChange(value)}
			className={cn(
				"flex size-9 items-center justify-center bg-card text-muted-foreground transition-colors",
				mode === value && "bg-primary/10 text-primary",
			)}
		>
			{icon}
		</button>
	);
	return (
		// biome-ignore lint/a11y/useSemanticElements: grupa przelacznikow, nie formularz
		<div
			role="group"
			aria-label="Widok tabeli"
			data-testid="table-mode-toggle"
			className={cn(
				"flex shrink-0 divide-x divide-border overflow-hidden rounded-md border border-border",
				className,
			)}
		>
			{item("list", "Lista", <List className="size-4" />)}
			{item("table", "Tabela", <Columns3 className="size-4" />)}
		</div>
	);
}

export interface MobileListRowContent {
	title: React.ReactNode;
	subtitle?: React.ReactNode;
	measure?: React.ReactNode;
	/** Udzial wartosci w maksimum modulu (0..1); null = bez paska. */
	bar?: { ratio: number; negative: boolean } | null;
	/**
	 * Druga linia pod miara (np. udzial z paskiem). Kolumna miary dostaje wtedy
	 * szerokosc tresci zamiast stalych 6,5rem.
	 */
	measureNote?: React.ReactNode;
	/** Wyroznienie wiersza (np. anomalia): tlo i akcent, wysokosc bez zmian. */
	className?: string;
}

export interface MobileMeasureOption {
	id: string;
	label: string;
}

interface MobileListViewProps {
	count: number;
	getKey: (index: number) => string;
	renderRow: (index: number) => MobileListRowContent;
	onOpen: (index: number) => void;
	/**
	 * Wlasny pasek nad lista zamiast przelacznika miary, sortowania, trybu
	 * i sumy. Strona dostarcza w nim przelacznik trybu i sortowanie.
	 */
	header?: React.ReactNode;
	measures: MobileMeasureOption[];
	measureId: string | null;
	onMeasureChange: (id: string) => void;
	onOpenSort?: () => void;
	sortBadge?: number;
	sortActive?: boolean;
	mode: TableMobileMode;
	onModeChange: (mode: TableMobileMode) => void;
	totalLabel: React.ReactNode;
	totalValue?: React.ReactNode;
	onFetchMore?: () => void;
	hasNextPage?: boolean;
	isFetchingNextPage?: boolean;
	isLoading?: boolean;
	loadingLabel?: string;
	emptyMessage?: string;
	testId?: string;
}

const ROW_HEIGHT = 58;

export function MobileListView({
	count,
	getKey,
	renderRow,
	onOpen,
	header,
	measures,
	measureId,
	onMeasureChange,
	onOpenSort,
	sortBadge = 0,
	sortActive = false,
	mode,
	onModeChange,
	totalLabel,
	totalValue,
	onFetchMore,
	hasNextPage,
	isFetchingNextPage,
	isLoading = false,
	loadingLabel = "Pobieranie danych...",
	emptyMessage = "Brak danych spelniajacych kryteria filtrowania.",
	testId,
}: MobileListViewProps) {
	const scrollRef = React.useRef<HTMLDivElement>(null);
	const sentinelRef = React.useRef<HTMLDivElement>(null);
	const virtualizer = useVirtualizer({
		count,
		getScrollElement: () => scrollRef.current,
		estimateSize: () => ROW_HEIGHT,
		overscan: 8,
	});

	React.useEffect(() => {
		const root = scrollRef.current;
		const sentinel = sentinelRef.current;
		if (!root || !sentinel || !onFetchMore || !hasNextPage) return;
		const io = new IntersectionObserver(
			([entry]) => {
				if (entry?.isIntersecting && hasNextPage && !isFetchingNextPage) onFetchMore();
			},
			{ root, rootMargin: "0px 0px 1200px 0px" },
		);
		io.observe(sentinel);
		return () => io.disconnect();
	}, [onFetchMore, hasNextPage, isFetchingNextPage]);

	return (
		<div className="relative flex min-h-0 flex-1 flex-col bg-card" data-testid={testId}>
			{header ?? (
				<>
					<div className="flex shrink-0 items-center gap-2 px-2.5 pt-2 pb-1">
						<MeasureSwitcher measures={measures} value={measureId} onChange={onMeasureChange} />
						{onOpenSort && (
							<button
								type="button"
								onClick={onOpenSort}
								aria-label="Sortowanie i filtry kolumn"
								data-testid="mobile-list-sort"
								className={cn(
									"relative flex size-9 shrink-0 items-center justify-center rounded-md border border-border bg-card text-muted-foreground",
									(sortActive || sortBadge > 0) && "text-primary",
								)}
							>
								<ArrowUpDown className="size-4" />
								{sortBadge > 0 && (
									<span className="absolute -top-1.5 -right-1.5 flex h-4 min-w-4 items-center justify-center rounded-sm bg-primary px-1 text-[10px] font-medium text-primary-foreground">
										{sortBadge}
									</span>
								)}
							</button>
						)}
						<TableModeToggle mode={mode} onChange={onModeChange} />
					</div>
					<div className="flex shrink-0 items-baseline justify-between gap-2 border-b border-border px-3 pt-1 pb-2 text-xs text-muted-foreground">
						<span className="min-w-0 truncate">{totalLabel}</span>
						{totalValue != null && (
							<span className="shrink-0 font-mono text-[15px] font-semibold tabular-nums text-foreground">
								{totalValue}
							</span>
						)}
					</div>
				</>
			)}
			<div
				ref={scrollRef}
				className={cn(
					"relative min-h-0 flex-1 overflow-y-auto overflow-x-hidden",
					isLoading && "pointer-events-none blur-[2px]",
				)}
				aria-busy={isLoading}
			>
				{count === 0 ? (
					<div className="flex h-24 items-center justify-center px-4 text-center text-sm text-muted-foreground">
						{emptyMessage}
					</div>
				) : (
					<div style={{ height: virtualizer.getTotalSize(), position: "relative" }}>
						{virtualizer.getVirtualItems().map((item) => {
							const content = renderRow(item.index);
							return (
								<MobileListRow
									key={getKey(item.index)}
									content={content}
									style={{ transform: `translateY(${item.start}px)`, height: ROW_HEIGHT }}
									onOpen={() => onOpen(item.index)}
								/>
							);
						})}
					</div>
				)}
				<div ref={sentinelRef} aria-hidden style={{ height: 1 }} />
				{isFetchingNextPage && (
					<div className="flex items-center justify-center gap-2 py-2 text-xs text-muted-foreground">
						<Loader2 className="size-3 animate-spin" />
						Ładowanie...
					</div>
				)}
			</div>
			{isLoading && (
				<div className="absolute inset-0 z-10 flex items-center justify-center bg-background/50">
					<span className="text-xs font-bold uppercase tracking-widest text-muted-foreground">
						{loadingLabel}
					</span>
				</div>
			)}
		</div>
	);
}

function MobileListRow({
	content,
	style,
	onOpen,
}: {
	content: MobileListRowContent;
	style: React.CSSProperties;
	onOpen: () => void;
}) {
	// Tresc wiersza jest `inert` (wypada z drzewa dostepnosci), wiec nazwe
	// przycisku dla czytnika ekranu bierzemy z jej tekstu po kazdym renderze.
	const rowRef = React.useRef<HTMLDivElement>(null);
	React.useLayoutEffect(() => {
		const row = rowRef.current;
		if (!row) return;
		const parts = Array.from(row.querySelectorAll("[data-row-text]"), (node) =>
			(node.textContent ?? "").replace(/\s+/g, " ").trim(),
		).filter(Boolean);
		const name = parts.join(", ");
		if (row.getAttribute("aria-label") !== name) row.setAttribute("aria-label", name);
	});
	return (
		// biome-ignore lint/a11y/useSemanticElements: wiersz listy moze zawierac przyciski z komorek (inert), <button> nie moze ich zagniezdzac
		<div
			ref={rowRef}
			role="button"
			tabIndex={0}
			data-testid="mobile-list-row"
			onClick={onOpen}
			onKeyDown={(e) => {
				if (e.key === "Enter" || e.key === " ") {
					e.preventDefault();
					onOpen();
				}
			}}
			className={cn(
				"absolute inset-x-0 top-0 flex cursor-pointer items-center gap-2.5 border-b border-border pr-1.5 pl-3 transition-colors active:bg-muted/70 focus-visible:bg-muted/70 focus-visible:outline-none",
				content.className,
			)}
			style={style}
		>
			{/* Komorki stron bywaja blokami (div, flex) — w wierszu listy plyna
			    jako tekst w jednej linii, inaczej lamalyby wysokosc 58 px. */}
			<div className="min-w-0 flex-1" inert>
				<div data-row-text className="truncate text-sm font-medium text-foreground [&_*]:inline">
					{content.title}
				</div>
				{content.subtitle != null && (
					<div data-row-text className="truncate text-xs text-muted-foreground [&_*]:inline">
						{content.subtitle}
					</div>
				)}
			</div>
			{content.measure != null && (
				<div
					className={cn(
						"shrink-0 text-right",
						content.measureNote != null ? "max-w-[55%]" : "w-[6.5rem]",
					)}
					inert
				>
					<div
						data-row-text
						className="truncate font-mono text-sm font-semibold tabular-nums text-foreground [&_*]:inline"
					>
						{content.measure}
					</div>
					{content.measureNote != null && (
						<div
							data-row-text
							className="mt-0.5 flex items-center justify-end gap-1.5 text-[11px] tabular-nums text-muted-foreground"
						>
							{content.measureNote}
						</div>
					)}
					{content.bar && (
						<div className="relative mt-1 h-1 overflow-hidden rounded-sm bg-muted">
							<i
								className={cn(
									"absolute inset-y-0 right-0 rounded-sm",
									content.bar.negative ? "bg-negative/70" : "bg-positive/70",
								)}
								style={{ width: `${Math.max(2, Math.round(content.bar.ratio * 100))}%` }}
							/>
						</div>
					)}
				</div>
			)}
			<ChevronRight className="size-4 shrink-0 text-muted-foreground/50" aria-hidden />
		</div>
	);
}

function MeasureSwitcher({
	measures,
	value,
	onChange,
}: {
	measures: MobileMeasureOption[];
	value: string | null;
	onChange: (id: string) => void;
}) {
	if (measures.length === 0) return <div className="min-w-0 flex-1" />;
	if (measures.length > 3) {
		return (
			<label className="flex min-w-0 flex-1 items-center">
				<span className="sr-only">Miara na liście</span>
				<select
					value={value ?? ""}
					onChange={(e) => onChange(e.target.value)}
					data-testid="mobile-measure-select"
					className="h-9 w-full min-w-0 rounded-md border border-border bg-card px-2 text-sm text-foreground"
				>
					{measures.map((m) => (
						<option key={m.id} value={m.id}>
							{m.label}
						</option>
					))}
				</select>
			</label>
		);
	}
	return (
		// biome-ignore lint/a11y/useSemanticElements: przelacznik segmentowy
		<div
			role="group"
			aria-label="Miara na liście"
			className="grid min-w-0 flex-1 divide-x divide-border overflow-hidden rounded-md border border-border"
			style={{ gridTemplateColumns: `repeat(${measures.length}, minmax(0, 1fr))` }}
		>
			{measures.map((m) => (
				<button
					key={m.id}
					type="button"
					aria-pressed={value === m.id}
					onClick={() => onChange(m.id)}
					className={cn(
						"h-9 min-w-0 truncate bg-background px-1.5 text-[12.5px] text-foreground",
						value === m.id && "bg-primary font-semibold text-primary-foreground",
					)}
				>
					{m.label}
				</button>
			))}
		</div>
	);
}

/* ============================================================================
   Arkusz sortowania i filtrow kolumn (tryb „Lista”)
   ============================================================================ */

export interface MobileSortOption {
	id: string;
	label: string;
	direction: "asc" | "desc" | false;
	/** Pierwszy klik sortuje malejaco (miary) zamiast rosnaco. */
	descFirst?: boolean;
}

export function MobileSortSheet({
	open,
	onOpenChange,
	options,
	onSort,
	filters,
	onClear,
	onPrimary,
	resultLabel,
}: {
	open: boolean;
	onOpenChange: (open: boolean) => void;
	options: MobileSortOption[];
	onSort: (id: string, desc: boolean | null) => void;
	filters?: { id: string; label: string; active: boolean; node: React.ReactNode }[];
	onClear: () => void;
	/** Wywolywane przez glowny przycisk tuz przed zamknieciem (np. zatwierdzenie wpisanych filtrow). */
	onPrimary?: () => void;
	resultLabel: string;
}) {
	return (
		<BottomSheet
			open={open}
			onOpenChange={onOpenChange}
			title="Sortowanie i filtry"
			data-testid="mobile-sort-sheet"
			footer={
				<SheetFooterActions
					onClear={onClear}
					primaryLabel={resultLabel}
					onPrimary={() => {
						onPrimary?.();
						onOpenChange(false);
					}}
				/>
			}
		>
			{options.length > 0 && (
				<section aria-label="Sortowanie" className="flex flex-col gap-0.5">
					<h3 className="px-1 pb-1 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
						Sortuj według
					</h3>
					{options.map((option) => (
						<div key={option.id} className="flex min-h-11 items-center gap-1 rounded-md px-1">
							<span
								className={cn(
									"min-w-0 flex-1 truncate text-sm",
									option.direction && "font-semibold text-primary",
								)}
							>
								{option.label}
							</span>
							<button
								type="button"
								aria-label={`Sortuj ${option.label} malejąco`}
								aria-pressed={option.direction === "desc"}
								onClick={() => onSort(option.id, option.direction === "desc" ? null : true)}
								className={cn(
									"flex size-9 items-center justify-center rounded-md border border-border",
									option.direction === "desc" && "border-primary bg-primary/10 text-primary",
								)}
							>
								<ArrowDown className="size-4" />
							</button>
							<button
								type="button"
								aria-label={`Sortuj ${option.label} rosnąco`}
								aria-pressed={option.direction === "asc"}
								onClick={() => onSort(option.id, option.direction === "asc" ? null : false)}
								className={cn(
									"flex size-9 items-center justify-center rounded-md border border-border",
									option.direction === "asc" && "border-primary bg-primary/10 text-primary",
								)}
							>
								<ArrowUp className="size-4" />
							</button>
						</div>
					))}
				</section>
			)}
			{filters && filters.length > 0 && (
				<section aria-label="Filtry kolumn" className="mt-3 flex flex-col gap-2">
					<h3 className="px-1 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
						Filtry kolumn
					</h3>
					{filters.map((filter) => (
						<div key={filter.id} className="px-1">
							<p
								className={cn(
									"text-xs text-muted-foreground",
									filter.active && "font-semibold text-primary",
								)}
							>
								{filter.label}
							</p>
							<div className="[&>div]:mt-0.5 [&>div]:h-auto [&>div]:border-t-0 [&>div]:pt-0 [&_button]:h-10 [&_input]:h-10 [&_input]:text-sm">
								{filter.node}
							</div>
						</div>
					))}
				</section>
			)}
		</BottomSheet>
	);
}

/* ============================================================================
   Pelnoekranowe szczegoly wiersza
   ============================================================================ */

export interface MobileDetailField {
	id: string;
	label: string;
	value: React.ReactNode;
	numeric?: boolean;
	/** Opis kolumny (na desktopie tooltip „?” w naglowku) — pod etykieta pola. */
	help?: React.ReactNode;
}

export function MobileRowDetails({
	open,
	onOpenChange,
	title,
	titleText,
	subtitle,
	kpis,
	fields,
	actions,
}: {
	open: boolean;
	onOpenChange: (open: boolean) => void;
	title: React.ReactNode;
	/** Tekst tytulu dla czytnikow ekranu, gdy `title` jest elementem. */
	titleText?: string;
	subtitle?: React.ReactNode;
	kpis: MobileDetailField[];
	fields: MobileDetailField[];
	actions?: React.ReactNode;
}) {
	const parentTitle = usePageChromeState()?.chrome?.title;
	return (
		<BottomSheet
			open={open}
			onOpenChange={onOpenChange}
			title={titleText || (typeof title === "string" ? title : "Szczegóły wiersza")}
			full
			data-testid="mobile-row-details"
			bodyClassName="px-0 pb-4"
			header={
				<header className="flex h-11 shrink-0 items-center gap-0.5 border-b border-border bg-card px-1">
					<button
						type="button"
						onClick={() => onOpenChange(false)}
						aria-label="Wstecz"
						className="flex size-10 shrink-0 items-center justify-center rounded-md text-muted-foreground hover:bg-accent"
					>
						<ChevronLeft className="size-5" />
					</button>
					<div className="min-w-0 flex-1 leading-tight">
						<span className="block truncate text-[11px] text-muted-foreground">
							{parentTitle ?? "Szczegóły"}
						</span>
						<div className="truncate text-[15px] font-semibold text-foreground [&_*]:inline">
							{title}
						</div>
					</div>
				</header>
			}
			footer={
				actions ? (
					<div className="flex w-full flex-wrap gap-2 [&_button]:min-h-11">{actions}</div>
				) : undefined
			}
		>
			{(subtitle != null || kpis.length > 0) && (
				<div className="border-b border-border px-3 pt-2.5 pb-3">
					{subtitle != null && (
						<div className="truncate text-xs text-muted-foreground [&_*]:inline">{subtitle}</div>
					)}
					{kpis.length > 0 && (
						<dl className="mt-2 grid grid-cols-3 gap-1.5">
							{kpis.map((kpi) => (
								<div
									key={kpi.id}
									className="min-w-0 rounded-md border border-border bg-background px-2 py-1.5"
								>
									<dt className="truncate text-[10.5px] uppercase tracking-wide text-muted-foreground">
										{kpi.label}
									</dt>
									<dd className="truncate font-mono text-sm font-semibold tabular-nums">
										{kpi.value}
									</dd>
								</div>
							))}
						</dl>
					)}
				</div>
			)}
			<h3 className="px-3 pt-3 pb-1 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
				Wszystkie kolumny ({fields.length})
			</h3>
			<dl>
				{fields.map((field) => (
					<div
						key={field.id}
						className="flex min-h-10 items-center justify-between gap-3 border-b border-border px-3 py-1.5"
					>
						<dt className="min-w-0 max-w-[45%] shrink-0 text-xs text-muted-foreground">
							{field.label}
							{field.help && (
								<span className="mt-0.5 block text-[11px] leading-snug text-muted-foreground/80">
									{field.help}
								</span>
							)}
						</dt>
						<dd
							className={cn(
								"flex min-w-0 flex-1 justify-end text-right text-sm text-foreground [overflow-wrap:anywhere]",
								field.numeric && "font-mono tabular-nums",
							)}
						>
							{field.value}
						</dd>
					</div>
				))}
			</dl>
		</BottomSheet>
	);
}
